import { Reducer } from 'redux'
import produce from 'immer'
import { responseDetailJokenKura, responseSummaryJokenKura } from 'types/ResponseTypes'
import { checkListDetail, checkListSummary } from 'types/SearchTypes'
import { isUniqueJokenKura, isUniqueJokenKuraDetail } from 'utils/booleanUtil'

export type TableJokenKuraState = {
  jokenList: responseSummaryJokenKura[]
  checkListJoken: checkListSummary[] // 選択している条件管理番号を保持
  checkListDetail: checkListDetail[] // 選択している明細を保持
  clickNo: string // 選択した条件管理NOを保持
}

const initialState: TableJokenKuraState = {
  jokenList: [],
  checkListJoken: [], // 選択している条件管理番号を保持
  checkListDetail: [], // 選択している明細を保持
  clickNo: '', // 選択した条件管理NOを保持
}

export type TableJokenKuraType =
  | 'TableJokenKuraAllCheckJoken' // 条件管理番号を全チェックする処理
  | 'TableJokenKuraCheckJoken' // 条件管理番号をチェックする処理
  | 'TableJokenKuraChangeCheckJoken' // 条件管理番号チェックリストを変更する処理
  | 'TableJokenKuraAllCheckShohin' // 明細を全チェックする処理
  | 'TableJokenKuraCheckShohin' // 明細をチェックする処理
  | 'TableJokenKuraFormatCheckSummary' // サマリチェックリストを初期化する処理
  | 'TableJokenKuraFormatCheckDetail' // 明細チェックリストを初期化する処理
  | 'TableJokenKuraFormat'
  | 'TableJokenKuraClickJoken' // 条件を選択する処理
  | 'TableJokenKuraChangeHininRiyu' // 否認理由を変更する処理
  | 'TableJokenKuraChangeCheckJokenDetail' // 明細チェックリストを変更する処理
  | 'TableJokenKuraChangeJochoHininRiyu' // 上長否認理由を変更する処理
  | 'dummy'

export type TableJokenKuraAction = {
  type: TableJokenKuraType
  jokenList?: responseSummaryJokenKura[]
  detailList?: responseDetailJokenKura[]
  isChecked?: boolean
  kanriNo?: string
  checkShohin?: checkListDetail
  checkData?: checkListSummary
  checkDataList?: checkListSummary[]
  checkListDetail?: checkListDetail[]
}

export const TableJokenKuraReducer: Reducer<TableJokenKuraState, TableJokenKuraAction> = produce((draft: TableJokenKuraState, action: TableJokenKuraAction) => {
  switch (action.type) {
    // 上長否認理由を変更する処理 //===========================//
    case 'TableJokenKuraChangeJochoHininRiyu': {
      const checkShohin = action.checkShohin!
      draft.checkListDetail = draft.checkListDetail.filter((r) => !isUniqueJokenKuraDetail(r, checkShohin))
      draft.checkListDetail.push(checkShohin)
      return
    }
    // 条件管理番号を全チェックする処理 //=========================//
    case 'TableJokenKuraAllCheckJoken': {
      const isChecked = action.isChecked!
      if (isChecked) {
        // チェック除去
        draft.checkListJoken = []
      } else {
        // チェック付与
        const selectList: checkListSummary[] = []
        action.jokenList!.forEach((r) =>
          selectList.push({
            JOKEN_KANRI_NO: r.JOKEN_KANRI_NO,
            TOKUI_CD: r.TOKUI_CD,
            JOTAI_KBN_0: r.JOTAI_KBN_0,
            JOTAI_KBN_1: r.JOTAI_KBN_1,
            JOTAI_KBN_2_1: r.JOTAI_KBN_2_1,
          }),
        )
        draft.checkListJoken = selectList
      }
      return
    }
    // 条件管理番号をチェックする処理 //=========================//
    case 'TableJokenKuraCheckJoken': {
      const checkData = action.checkData!
      const isChecked = action.isChecked!
      if (isChecked) {
        // チェック除去
        draft.checkListJoken = draft.checkListJoken.filter((r) => !isUniqueJokenKura(r, checkData))
      } else {
        // チェック付与
        draft.checkListJoken.push(checkData)
      }
      return
    }
    // 条件管理番号チェックリストを変更する処理 //=========================//
    case 'TableJokenKuraChangeCheckJoken': {
      draft.checkListJoken = action.checkDataList!
      return
    }
    // 明細を全チェックする処理 //================================//
    case 'TableJokenKuraAllCheckShohin': {
      const isChecked = action.isChecked!
      const stateList = draft.checkListDetail
      if (isChecked) {
        // チェック除去
        const selectList: checkListDetail[] = []
        for (let i = 0; i < stateList.length; i++) {
          selectList.push(stateList[i].isChecked ? { ...stateList[i], isChecked: false } : stateList[i])
        }
        draft.checkListDetail = selectList
      } else {
        // チェック付与
        const detailList = action.detailList!
        const originList: checkListDetail[] = []
        for (let i = 0; i < detailList.length; i++) {
          originList.push({
            isChecked: true,
            JOTAI_KBN: detailList[i].JOTAI_KBN,
            KAKUNIN_KBN: detailList[i].KAKUNIN_KBN,
            ADD_YMD: detailList[i].ADD_YMD,
            ADD_HNS: detailList[i].ADD_HNS,
            JP_CD: detailList[i].JP_CD,
            NET: detailList[i].NET,
            HININ_RIYU: detailList[i].HININ_RIYU,
            JOCHO_HININ_RIYU: detailList[i].JOCHO_HININ_RIYU,
          })
        }
        for (let i = 0; i < stateList.length; i++) {
          for (let j = 0; j < originList.length; j++) {
            if (isUniqueJokenKuraDetail(stateList[i], originList[j])) {
              originList.splice(j, 1)
              originList.push({ ...stateList[i], isChecked: true })
              break
            }
          }
        }
        draft.checkListDetail = originList
      }
      return
    }
    // 明細をチェックする処理 //================================//
    case 'TableJokenKuraCheckShohin': {
      const checkShohin = action.checkShohin!
      const isChecked = action.isChecked!
      const selectList = draft.checkListDetail.filter((r) => isUniqueJokenKuraDetail(r, checkShohin))
      if (selectList.length <= 0) {
        draft.checkListDetail.push({ ...checkShohin, isChecked: !isChecked })
      } else {
        draft.checkListDetail = draft.checkListDetail.filter((r) => !isUniqueJokenKuraDetail(r, checkShohin))
        draft.checkListDetail.push({ ...selectList[0], isChecked: !isChecked })
      }
      return
    }
    // サマリチェックリストを初期化する処理 //================================//
    case 'TableJokenKuraFormatCheckSummary': {
      draft.checkListJoken = initialState.checkListJoken
      return
    }
    // 明細チェックリストを初期化する処理 //================================//
    case 'TableJokenKuraFormatCheckDetail': {
      draft.checkListDetail = initialState.checkListDetail
      return
    }
    // 否認理由を変更する処理 //===========================//
    case 'TableJokenKuraChangeHininRiyu': {
      const checkShohin = action.checkShohin!
      draft.checkListDetail = draft.checkListDetail.filter((r) => !isUniqueJokenKuraDetail(r, checkShohin))
      draft.checkListDetail.push(checkShohin)
      return
    }
    // 明細チェックリストを変更する処理 //=========================//
    case 'TableJokenKuraChangeCheckJokenDetail': {
      draft.checkListDetail = action.checkListDetail!
      return
    }
    case 'TableJokenKuraFormat': {
      for (const key in draft) {
        const tempKey: keyof TableJokenKuraState = key as keyof TableJokenKuraState
        ;(draft as any)[tempKey] = initialState[tempKey]
      }
      return
    }
    // 条件を選択する処理 //===========================//
    case 'TableJokenKuraClickJoken': {
      draft.clickNo = action.kanriNo!
      draft.checkListDetail = initialState.checkListDetail
      return
    }
  }
}, initialState)
